import cx from "classnames";
import React, { useRef, useState } from "react";

import { LoadingSkeleton } from "@moment/design-system/LoadingSkeleton";
import { ValueError } from "@moment/design-system/ValueError";

import {
	isFulfilledResult,
	isPendingResult,
	isRejectedResult,
} from "~/components/canvas/easel/runtime/interfaces";
import { Inspector } from "~/components/inspectorv2";
import { type CachedCell, type CellInfo } from "~/models/cell";

import { AtlasErrorInfo } from "./AtlasErrorInfo";
import { isAtlasError } from "./AtlasErrorInfo/AtlasError";
import { CellUpdateIndicator } from "./CellUpdateIndicator";
import { ImportView } from "./ImportView";
import { formatCellErrorMessage } from "./formatCellErrorMessage";

const shouldRenderImport = (cellInfo: CellInfo, cachedCell: CachedCell): boolean => {
	const staticAnalysis = cellInfo.analysis?.inspectInfo;
	if (cachedCell.result?.type === "REJECTED") {
		return false;
	}

	return (
		staticAnalysis?.kind === "NamespacedCanvasImportInfo" ||
		staticAnalysis?.kind === "PageImportInfo"
	);
};

/**
 * ::: Computed Result :::
 * This component renders the results of a computed cell
 */

export type Selection = "none" | "selected" | "multiselected";

type Props = {
	cellInfo: CellInfo;
	cachedCell: CachedCell;
};

export const ComputedResult = React.memo<Props>(function ComputedResult({ cellInfo, cachedCell }) {
	const { result } = cachedCell;

	const [resolvedAtLeastOnce, setResolvedAtLeastOnce] = useState(false);
	const wasPreviouslyError = useRef(false);

	if (cellInfo.status === "error") {
		const error = cellInfo.message.split(/\r?\n\n/);
		const message = error[0];
		const additionalInfo = formatCellErrorMessage(cellInfo.message);
		wasPreviouslyError.current = true;
		return (
			<ValueError
				collapsedByDefault={false}
				message={message === undefined ? "Error" : message}
				additionalInfo={additionalInfo}
			/>
		);
	}

	if (shouldRenderImport(cellInfo, cachedCell)) {
		return (
			<div className={`cursor-default flex-col overflow-x-auto whitespace-nowrap p-2`}>
				<ImportView cellInfo={cellInfo} />
			</div>
		);
	}

	if (result?.staticError !== undefined) {
		wasPreviouslyError.current = true;
		return (
			<ValueError
				message={`Static Error: ${result.staticError.message}`}
				additionalInfo={result.staticError.stack}
				originalError={result.staticError}
			/>
		);
	}

	if (isRejectedResult(result)) {
		const e = result.runtimeError;
		wasPreviouslyError.current = true;
		if (isAtlasError(e)) {
			return (
				<>
					<CellUpdateIndicator result={result} />
					<ValueError
						message={`Atlas Error: ${e.message}`}
						additionalInfo={<AtlasErrorInfo error={e} />}
						originalError={e}
					/>
				</>
			);
		}
		return (
			<>
				<CellUpdateIndicator result={result} />
				<ValueError
					message={`Runtime Error: ${e.message}`}
					additionalInfo={e.stack}
					originalError={e}
				/>
			</>
		);
	}

	// We want to show the loading skeleton when either:
	// 1. it's the first time we're resolving the cell
	// 2. the cell was previously in an error state
	if (result === undefined || isPendingResult(result)) {
		if (wasPreviouslyError.current || !resolvedAtLeastOnce) {
			return (
				<div className={"p-2"}>
					<LoadingSkeleton
						data-testid="loading-cell-skeleton"
						baseKey={`palette-loader-${cellInfo.cell.id}`}
					/>
				</div>
			);
		}
	} else if (!resolvedAtLeastOnce) {
		setResolvedAtLeastOnce(true);
	}

	if (isFulfilledResult(result)) {
		wasPreviouslyError.current = false;
	}

	return (
		<div className="flex flex-row items-stretch">
			<div
				className={cx(
					"cursor-default",
					"flex-col",
					"flex-grow",

					// This is the class that allows compute cells to x-scroll infinitely to the
					// right. For example, the standard datagrid component.
					"prosemirror-compute-cell--computed-result-container"
				)}
			>
				<Inspector cellInfo={cellInfo} cachedCell={cachedCell} />
			</div>
		</div>
	);
});
